import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  CreditCard,
  ExternalLink,
  InProgressTask,
  Plus,
  Settings,
  Wallet,
  Warning,
} from '@factorialco/f0-react/icons/app'
import { Features } from 'generated/featureDefinitions'
import {
  AccountsListPageDocument,
  AccountsListPageQuery,
  BankingAccountDetailedFragment,
  BankingBankAccountFragment,
  BankingOnboardingFragment,
  CurrencySelectorCurrencyFragment,
  LegalEntityForSelectFragment,
  ReconnectBankDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AccountsUpsellCard } from 'modules/banking/components/AccountsUpsellCard'
import { FininshSetupAccountModal } from 'modules/banking/components/FinishSetupAccountModal'
import { AddAccountModalF0, NewAccountModal } from 'modules/banking/components/NewAccountModal'
import { useBankingAuthorization } from 'modules/banking/hooks/useBankingAuthorization'
import { bankingAccountActions, getContinueOnboardingUrl } from 'modules/banking/utils'
import { accountsPayableUpsellRequestProps } from 'modules/finance/lib/upsellUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { UpsellingKit } from 'modules/upselling_kit/components'

import { getEmptyStates } from './cardVisualization'
import { mapAccountsToListItems } from './mapAccountsToListItems'
import { AccountListItem } from './types'

interface SearchParamsType {
  setup_membership?: string
}

const DATA_COLLECTION_STORAGE = { features: ['grouping' as const] }

export const AccountsListPageNew = () => {
  const {
    company: { id: companyId },
    employee,
    access: { locale },
  } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const searchParams = useSearchParams<SearchParamsType>()
  const [showNewModal, setShowNewModal] = useState<boolean>(false)
  const [showUpsellModal, setShowUpsellModal] = useState<boolean>(false)
  const [showFinishSetupModal, setShowFinishSetupModal] = useState<boolean>(false)
  const [selectedAccount, setSelectedAccount] = useState<
    BankingAccountDetailedFragment | undefined
  >()
  const [selectedBankAccount, setSelectedBankAccount] = useState<
    BankingBankAccountFragment | undefined
  >()
  const { getAuthorizationUrl } = useBankingAuthorization()
  const membershipSetupProcessed = useRef(false)
  // Track if we've ever seen an account with import_data service (doesn't change with filters)
  const hasImportDataServiceRef = useRef<boolean | undefined>(undefined)

  // State to track items for group counting
  const [items, setItems] = useState<AccountListItem[]>([])

  // Store data from initial query for modals and policies
  const [queryData, setQueryData] = useState<{
    currencies: CurrencySelectorCurrencyFragment[]
    legalEntities: LegalEntityForSelectFragment[]
    onboardings: BankingOnboardingFragment[]
    bankAccountsManage: boolean
    bankAccountsSeeDetailPage: boolean
    sidebarSeeFinanceAccountsSubLevelItem: boolean
    accountsPayableAvailable: boolean
    paymentsFeatureAvailable: boolean
    swanEnabled: boolean
    hasAccountWithImportDataService: boolean
  }>({
    currencies: [],
    legalEntities: [],
    onboardings: [],
    bankAccountsManage: false,
    bankAccountsSeeDetailPage: false,
    sidebarSeeFinanceAccountsSubLevelItem: true, // Default to true until loaded
    accountsPayableAvailable: false,
    paymentsFeatureAvailable: false,
    swanEnabled: false,
    hasAccountWithImportDataService: false,
  })

  const featureIds = [Features.accounts_payable_management, Features.finance_payments_core]

  const [reconnectBank] = useMutation(ReconnectBankDocument, {
    extractorFn: (mutationData) =>
      mutationData.bankingMutations.reconnectExternalBankBankingOnboarding,
  })

  // Handle setup_membership parameter from invitation email
  useEffect(() => {
    const setupMembershipId = searchParams.setup_membership

    // Only process once per page load
    if (setupMembershipId && employee?.id && !membershipSetupProcessed.current) {
      membershipSetupProcessed.current = true

      const handleMembershipSetup = async () => {
        try {
          app.setProgress({
            message: i18n.t('banking.accounts.membership_setup.authorizing'),
          })

          const currentUrl = window.location.origin + window.location.pathname
          const authorizationUrl = await getAuthorizationUrl(employee.id, currentUrl, {
            action: 'bind_account_membership',
            targetId: parseInt(setupMembershipId, 10),
          })

          if (authorizationUrl) {
            // Redirect to Swan for authorization
            window.location.href = authorizationUrl
          } else {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('banking.accounts.membership_setup.error'),
            })
            app.setProgress(null)
          }
        } catch (setupError) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('banking.accounts.membership_setup.error'),
          })
          app.setProgress(null)
        }
      }

      handleMembershipSetup()
    }
  }, [searchParams.setup_membership, employee?.id, getAuthorizationUrl])

  const handleReconnect = useCallback(
    async (account: BankingAccountDetailedFragment) => {
      if (!account.institution?.id || !account.onboarding.legalEntity.id) {
        return
      }

      app.setProgress({
        message: i18n.t('banking.accounts.new_wizard.notifications.connecting_bank'),
      })

      const result = await reconnectBank({
        variables: {
          institutionId: account.institution.id,
          legalEntityId: account.onboarding.legalEntity.id,
        },
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('banking.accounts.new_wizard.notifications.error'),
        })
      })

      if (handled.success) {
        if (handled.data.onboarding?.onboardingUrl) {
          window.open(handled.data.onboarding.onboardingUrl, '_self')
        }
      }
    },
    [reconnectBank]
  )

  const handleOnboardingClick = useCallback(
    async (onboarding: BankingOnboardingFragment) => {
      // Check if user needs to check their email instead of redirecting
      const needsEmailCheck = onboarding.requiredActions?.includes('check_email')
      // Check if user needs to complete identification (OAuth flow)
      const needsIdentification = onboarding.requiredActions?.includes('complete_identification')

      if (needsEmailCheck) {
        // Show email notification
        app.setFlash({
          type: 'correct',
          message: i18n.t('banking.onboarding.check_email_modal.description'),
        })
      } else if (needsIdentification) {
        try {
          app.setProgress({
            message: i18n.t('banking.accounts.membership_setup.authorizing'),
          })

          const currentUrl = window.location.origin + window.location.pathname
          const authorizationUrl = await getAuthorizationUrl(employee.id, currentUrl, {
            action: 'retry_identification',
            targetId: onboarding.id,
          })

          if (authorizationUrl) {
            window.location.href = authorizationUrl
          } else {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('banking.accounts.membership_setup.error'),
            })
            app.setProgress(null)
          }
        } catch (error) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('banking.accounts.membership_setup.error'),
          })
          app.setProgress(null)
        }
      } else {
        const url = getContinueOnboardingUrl(onboarding, [])
        if (url) {
          window.open(url, '_self')
        }
      }
    },
    [employee.id, getAuthorizationUrl]
  )

  const handleAccountClick = useCallback(
    (account: BankingAccountDetailedFragment, bankAccount: BankingBankAccountFragment) => {
      const { hasToFinishSetup, hasToReconnect } = bankingAccountActions(account, bankAccount)
      setSelectedAccount(account)
      setSelectedBankAccount(bankAccount)

      if (hasToReconnect) {
        handleReconnect(account)
      } else if (hasToFinishSetup) {
        setShowFinishSetupModal(true)
      } else {
        if (queryData.bankAccountsSeeDetailPage) {
          navigate(navigation.finance.banking.accounts.accountId.fullPath({ id: bankAccount.id }))
        }
      }
    },
    [handleReconnect, navigate, navigation, queryData.bankAccountsSeeDetailPage]
  )

  const handleItemClick = useCallback(
    (item: AccountListItem) => {
      if (item.onboarding) {
        handleOnboardingClick(item.onboarding)
      } else if (item.account && item.bankAccount) {
        handleAccountClick(item.account, item.bankAccount)
      }
    },
    [handleOnboardingClick, handleAccountClick]
  )

  const getGroupCount = useCallback(
    (groupId: unknown) => {
      if (groupId !== 'in_review' && groupId !== 'active') return 0
      return items.filter((i) => i.groupStatus === groupId).length
    },
    [items]
  )

  const source = useGraphqlDataCollectionSource({
    document: AccountsListPageDocument,
    mapData: (data: AccountsListPageQuery) => {
      const accounts = data?.banking?.accounts ?? []
      const onboardings = data?.banking?.onboardings ?? []
      const currencies = data?.apiCore?.currencies ?? []
      const legalEntities =
        data?.companies?.legalEntitiesConnection?.edges?.map((e) => e.node) ?? []
      const { bankAccountsManage, bankAccountsSeeDetailPage } = data?.banking?._policies ?? {
        bankAccountsManage: { can: false },
        bankAccountsSeeDetailPage: { can: false },
      }
      const { sidebarSeeFinanceAccountsSubLevelItem } = data?.finance?._policies ?? {
        sidebarSeeFinanceAccountsSubLevelItem: { can: false },
      }

      const featureAvailabilities =
        data?.companies?.featureAvailabilitiesConnection?.edges?.map((e) => e.node) ?? []
      const accountsPayableAvailable =
        featureAvailabilities.find((node) => node.id === Features.accounts_payable_management)
          ?.available ?? false
      const paymentsFeatureAvailable =
        featureAvailabilities.find((node) => node.id === Features.finance_payments_core)
          ?.available ?? false

      const features = data?.features?.featuresConnection?.edges?.map((e) => e.node) ?? []
      const swanEnabled =
        features.find((feature) => feature.name === 'dev_finance_swan_reactivation')?.enabled ??
        false

      // Only calculate hasAccountWithImportDataService on first load (without filters)
      // This prevents the upsell card from appearing/disappearing based on filter selection
      if (hasImportDataServiceRef.current === undefined) {
        hasImportDataServiceRef.current = accounts.some((el) =>
          el.providerConfiguration?.services.includes('import_data')
        )
      }

      // Update query data for modals and policies
      setQueryData({
        currencies,
        legalEntities,
        onboardings,
        bankAccountsManage: bankAccountsManage?.can ?? false,
        bankAccountsSeeDetailPage: bankAccountsSeeDetailPage?.can ?? false,
        sidebarSeeFinanceAccountsSubLevelItem: sidebarSeeFinanceAccountsSubLevelItem?.can ?? false,
        accountsPayableAvailable,
        paymentsFeatureAvailable,
        swanEnabled,
        hasAccountWithImportDataService: hasImportDataServiceRef.current ?? false,
      })

      // Map data to list items
      const mapped = mapAccountsToListItems(accounts, onboardings, locale)
      setItems(mapped)
      return mapped
    },
    mapVariables: ({ filters, search }) => ({
      companyId,
      featureIds,
      legalEntityIds: filters.legalEntityId?.length ? filters.legalEntityId : undefined,
      search: search || undefined,
    }),
    mapPagination: (data: AccountsListPageQuery) => {
      const accounts = data?.banking?.accounts ?? []
      const onboardings = data?.banking?.onboardings ?? []
      // Count onboardings not completed + all bank accounts from completed accounts
      const onboardingCount = onboardings.filter(
        (o: BankingOnboardingFragment) => o.status !== 'completed'
      ).length
      const bankAccountCount = accounts
        .filter((a: BankingAccountDetailedFragment) => a.onboarding?.status === 'completed')
        .reduce((acc: number, a: BankingAccountDetailedFragment) => acc + a.bankAccounts.length, 0)
      return { total: onboardingCount + bankAccountCount }
    },
    search: { enabled: true },
    filters: {
      legalEntityId: {
        type: 'in',
        label: i18n.t('common.legal_entity'),
        options: {
          options: queryData.legalEntities.map((le) => ({
            value: le.id,
            label: le.legalName,
          })),
        },
      },
    },
    grouping: {
      collapsible: true,
      defaultOpenGroups: true,
      mandatory: true,
      hideSelector: true,
      groupBy: {
        groupStatus: {
          name: i18n.t('banking.accounts.group_by.status'),
          defaultDirection: 'asc' as const,
          label: (groupId: unknown) =>
            groupId === 'in_review'
              ? i18n.t('banking.accounts.status.in_review')
              : i18n.t('banking.accounts.status.active'),
          itemCount: getGroupCount,
        },
      },
    },
    primaryActions: () => {
      if (
        !queryData.bankAccountsManage ||
        (!queryData.accountsPayableAvailable && !queryData.paymentsFeatureAvailable)
      ) {
        return undefined
      }
      return {
        label: i18n.t('banking.accounts.actions.add_account'),
        icon: Plus,
        onClick: () => setShowNewModal(true),
      }
    },
    itemOnClick: (item: AccountListItem) => () => handleItemClick(item),
    itemActions: (item: AccountListItem) => {
      const actions: Array<{
        label: string
        icon?: typeof Settings
        onClick: () => void
        type: 'primary' | 'secondary' | 'other'
      }> = []

      // Onboardings with required actions
      if (item.groupStatus === 'in_review' && item.onboarding && item.actionText) {
        actions.push({
          label: item.actionText,
          icon: ExternalLink,
          onClick: () => handleItemClick(item),
          type: 'secondary',
        })
      }

      // Active accounts that need finish setup
      if (item.groupStatus === 'active' && item.account && item.bankAccount) {
        if (item.hasToFinishSetup) {
          actions.push({
            label: i18n.t('banking.accounts.finish_setup'),
            icon: Settings,
            onClick: () => handleItemClick(item),
            type: 'secondary',
          })
        }
      }

      return actions
    },
  })

  const showAccountsPayableUpsellCard =
    !queryData.accountsPayableAvailable || !queryData.hasAccountWithImportDataService
  const upsellCardAction = queryData.accountsPayableAvailable ? 'add_account' : 'contact_us'
  const upsellRequestProps = accountsPayableUpsellRequestProps('ap_bankaccounts_upsellcard')

  return (
    <ResponsivePage.Body
      showHeader
      variant="full"
      authorized={queryData.sidebarSeeFinanceAccountsSubLevelItem}
    >
      <>
        {showNewModal &&
          (queryData.swanEnabled ? (
            <AddAccountModalF0
              legalEntities={queryData.legalEntities}
              currencies={queryData.currencies}
              onClose={() => setShowNewModal(false)}
              accountingEnabled={queryData.accountsPayableAvailable}
              existingOnboardings={queryData.onboardings}
              showAccountPurposeSelector={queryData.swanEnabled}
            />
          ) : (
            <NewAccountModal
              legalEntities={queryData.legalEntities}
              currencies={queryData.currencies}
              onClose={() => setShowNewModal(false)}
              accountingEnabled={queryData.accountsPayableAvailable}
              existingOnboardings={queryData.onboardings}
              showAccountPurposeSelector={false}
            />
          ))}
        {showUpsellModal && (
          <UpsellingKit.FeatureModal
            moduleRequested={upsellRequestProps.moduleRequested}
            requestSource="ap_bankaccounts_upsellcard"
            requestReason={upsellRequestProps.requestReason}
            featureRequested={
              upsellRequestProps.featureRequested || Features.accounts_payable_management
            }
            isOpen={showUpsellModal}
            onClose={() => setShowUpsellModal(false)}
          />
        )}
        {showFinishSetupModal && selectedBankAccount && selectedAccount && (
          <FininshSetupAccountModal
            bankAccount={selectedBankAccount}
            legalEntityId={selectedAccount.legalEntity.id}
            companyId={companyId}
            onClose={() => setShowFinishSetupModal(false)}
            onFinish={() => setShowFinishSetupModal(false)}
          />
        )}
        {showAccountsPayableUpsellCard && (
          <Box width="full" paddingBottom={'s48'}>
            <AccountsUpsellCard
              mode={upsellCardAction}
              onAddAccountClick={() => {
                setShowUpsellModal(false)
                setShowNewModal(true)
              }}
              onContactUsClick={() => {
                setShowUpsellModal(true)
              }}
            />
          </Box>
        )}
        <OneDataCollection
          id="banking/accounts/v1"
          storage={DATA_COLLECTION_STORAGE}
          source={source}
          emptyStates={getEmptyStates()}
          visualizations={[
            {
              type: 'card',
              options: {
                title: (item: AccountListItem) => {
                  if (item.groupStatus === 'active' && item.balanceText) {
                    return item.balanceText
                  }
                  return item.title
                },
                description: (item: AccountListItem) => {
                  if (item.groupStatus === 'active') {
                    return item.institutionName || item.title
                  }
                  return item.institutionName || ''
                },
                avatar: (item: AccountListItem) => ({
                  type: 'team' as const,
                  name: item.institutionName || item.title,
                  src: item.logoUrl || '',
                }),
                cardProperties: [
                  {
                    label: '',
                    icon: InProgressTask,
                    render: (item: AccountListItem) => {
                      if (item.groupStatus !== 'in_review') return undefined
                      if (!item.statusText) return undefined
                      return {
                        type: 'status' as const,
                        value: {
                          status: item.statusVariant || 'neutral',
                          label: item.statusText,
                        },
                      }
                    },
                  },
                  {
                    label: '',
                    icon: CreditCard,
                    render: (item: AccountListItem) => {
                      if (!item.accountLastDigits) return undefined
                      return {
                        type: 'status' as const,
                        value: {
                          status: 'positive',
                          label: item.accountLastDigits,
                        },
                      }
                    },
                  },
                  {
                    label: '',
                    icon: Wallet,
                    render: (item: AccountListItem) => {
                      if (!item.description) return undefined
                      return {
                        type: 'text' as const,
                        value: item.description,
                      }
                    },
                  },
                  {
                    label: '',
                    icon: Warning,
                    render: (item: AccountListItem) => {
                      // Only show alert for reconnect needed (not for onboarding actions which now have buttons)
                      if (!item.hasAlert || item.groupStatus === 'in_review') return undefined

                      const statusVariant = item.alertLevel === 'critical' ? 'critical' : 'warning'

                      if (item.alertMessage) {
                        return {
                          type: 'status' as const,
                          value: {
                            label: item.alertMessage,
                            status: statusVariant,
                          },
                        }
                      }

                      return undefined
                    },
                  },
                ],
              },
            },
          ]}
        />
      </>
    </ResponsivePage.Body>
  )
}

// Named export only - used by index.tsx with feature flag
